import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit2,
  Loader2,
  Upload,
  X,
  ChevronDown,
  Calendar as CalendarIcon,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useCreateCalendarMutation,
  useGetCalendarByAcademyYearQuery,
  useGetCalendarsQuery,
  useGetCalendarByIdQuery,
} from "../../slices/calendarApiSlice";
import {
  useGetUsersQuery,
  useGetAcademyQuery,
  useGetUserByEmailQuery,
} from "../../slices/userApiSlice";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CreateCalendar = () => {
  const navigate = useNavigate();

  // Get user info from Redux
  const userInfo = useSelector((state) => state.auth.userInfo);
  const userEmail = userInfo?.username || userInfo?.email;
  const academyId = userInfo?.academy_id || userInfo?.academyId;

  // API queries
  const { data: userByEmail } = useGetUserByEmailQuery(userEmail, {
    skip: !userEmail,
  });
  const { data: academies, isLoading: isLoadingAcademies } =
    useGetAcademyQuery();
  const { data: allUsers, isLoading: isLoadingUsers } = useGetUsersQuery();

  // API mutation
  const [createCalendar, { isLoading: isCreating }] =
    useCreateCalendarMutation();

  const [currentStep, setCurrentStep] = useState(1);
  const currentYear = new Date().getFullYear();
  const [calendarData, setCalendarData] = useState({
    name: "",
    year: currentYear,
    weekendSelection: {
      saturday: true,
      sunday: true,
    },
    casualLeavePerYear: 10,
    earnedLeaveIncrement: 1.25,
    maxOvertimeHours: 40,
  });
  const [holidays, setHolidays] = useState([]);
  const [users, setUsers] = useState([]);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayForm, setHolidayForm] = useState({
    name: "",
    date: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showUserEditModal, setShowUserEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({
    casualLeave: 0,
    earnedLeave: 0,
  });
  const [yearError, setYearError] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState(null); // "excel" or "calendar"
  const [selectedCalendarId, setSelectedCalendarId] = useState("");

  // State for Alert Dialogs
  const [deleteUserAlertOpen, setDeleteUserAlertOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Get the actual academy ID from user data
  const actualAcademyId = userByEmail?.user?.academyId || academyId;

  // Fetch existing calendars for import
  const { data: existingCalendars = [], isLoading: isLoadingCalendars } =
    useGetCalendarsQuery(
      { academy_id: actualAcademyId },
      { skip: !actualAcademyId },
    );

  // Fetch selected calendar for import
  const { data: selectedCalendarData } = useGetCalendarByIdQuery(
    selectedCalendarId,
    {
      skip: !selectedCalendarId || importType !== "calendar",
    },
  );

  // Check if calendar already exists for the year using existing API
  const {
    data: existingCalendar,
    refetch: checkExistingCalendar,
    isLoading: isLoadingCalendar,
  } = useGetCalendarByAcademyYearQuery(
    {
      academy_id: actualAcademyId,
      year: calendarData.year,
    },
    { skip: !actualAcademyId || !calendarData.year },
  );

  // Validate year in real-time when year or query data changes
  useEffect(() => {
    if (!calendarData.year || !actualAcademyId) {
      setYearError("");
      return;
    }

    const currentYearValue = calendarData.year;

    // Immediately check if year is in the past (no API call needed)
    if (currentYearValue < currentYear) {
      setYearError(
        `Year must be ${currentYear} or later. Past years are not allowed.`,
      );
      return;
    }

    // If query is still loading, optimistically clear error for valid years
    // (The onChange handler already cleared it, but this ensures it stays clear)
    if (isLoadingCalendar) {
      // Keep error clear while loading - we'll check for duplicates when the query completes
      if (currentYearValue >= currentYear) {
        setYearError("");
      }
      return;
    }

    // After query completes, check if calendar exists for the CURRENT year value
    // Only check if the query result is actually for the current year (not stale cached data)
    if (existingCalendar?.calendar) {
      const calendarYear = existingCalendar.calendar.year;
      // Only show error if the calendar is actually for the current year value
      if (calendarYear === currentYearValue) {
        setYearError("A calendar for this year already exists");
        return;
      }
      // If calendar exists but is for a different year, ignore it (stale data)
    }

    // Clear error if year is valid and no duplicate exists
    // This will clear the error immediately when switching to a year without a duplicate
    setYearError("");
  }, [
    existingCalendar,
    calendarData.year,
    actualAcademyId,
    currentYear,
    isLoadingCalendar,
  ]);

  const checkYearExists = async (year) => {
    if (!year || !actualAcademyId) return false;

    try {
      const result = await checkExistingCalendar();
      const exists = result?.data?.calendar ? true : false;
      return exists;
    } catch (error) {
      if (error?.status === 404 || error?.originalStatus === 404) {
        return false; // This is fine, calendar doesn't exist
      }
      return false;
    }
  };

  // Validate year input
  const validateYear = async (year) => {
    setYearError("");

    if (!year) {
      setYearError("Year is required");
      return false;
    }

    // Only allow current year and future years (not past years)
    if (year < currentYear) {
      setYearError(
        `Year must be ${currentYear} or later. Past years are not allowed.`,
      );
      return false;
    }

    // Set a reasonable upper limit (e.g., 10 years from now)
    const maxYear = currentYear + 10;
    if (year > maxYear) {
      setYearError(`Year cannot be more than ${maxYear}`);
      return false;
    }

    // Check if calendar already exists for this year
    const exists = await checkYearExists(year);
    if (exists) {
      setYearError("A calendar for this year already exists");
      return false;
    }

    return true;
  };

  // Populate users from API data
  useEffect(() => {
    if (allUsers && actualAcademyId) {
      // Filter users by academy, exclude admin/superadmin roles, and transform to the expected format
      const academyUsers = allUsers
        .filter((user) => {
          const userAcademyId = user.academyId || user.academy_id;
          const isSameAcademy = userAcademyId === actualAcademyId;
          const userRole =
            user.role?.name ||
            user.userRole?.name ||
            user.role ||
            user.userRole;
          const isNotAdmin = ![
            "admin",
            "Admin",
            "super_admin",
            "Super Admin",
            "superadmin",
            "SUPERADMIN",
            "ADMIN",
          ].includes(userRole);

          return isSameAcademy && isNotAdmin;
        })
        .map((user) => ({
          id: user.userId || user.id,
          name: user.name,
          email: user.email,
          employeeId: user.employeeId,
          casualLeave: calendarData.casualLeavePerYear, // Use config value as default
          earnedLeave: 0, // Default earned leave is 0
          profile: user.image || null,
        }));

      setUsers(academyUsers);
    }
  }, [allUsers, actualAcademyId, calendarData.casualLeavePerYear]);

  // Show loading state while fetching data
  if (isLoadingUsers || isLoadingAcademies) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Loading user data...</p>
        </div>
      </div>
    );
  }

  // Show error if no academy ID is found
  if (!actualAcademyId) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Academy Found
          </h3>
          <p className="text-gray-500 mb-4">
            Unable to determine your academy. Please contact support.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const roleBasePath = pathSegments[0];
    navigate(`/${roleBasePath}/calendar`);
  };

  const handleNext = async () => {
    if (currentStep < 3) {
      // If moving from step 1 to step 2, validate year first
      if (currentStep === 1) {
        const isYearValid = await validateYear(calendarData.year);
        if (!isYearValid) {
          // Don't proceed to next step if year validation fails
          return;
        }
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveHoliday = () => {
    if (!holidayForm.name.trim() || !holidayForm.date) {
      setFormErrors({
        name: !holidayForm.name.trim() ? "Holiday name is required" : "",
        date: !holidayForm.date ? "Date is required" : "",
      });
      return;
    }

    const newHoliday = {
      id: Date.now(),
      name: holidayForm.name,
      date: holidayForm.date,
    };

    setHolidays([...holidays, newHoliday]);
    setHolidayForm({ name: "", date: "" });
    setShowHolidayModal(false);
    setFormErrors({});
  };

  const handleDeleteHoliday = (holidayId) => {
    setHolidays(holidays.filter((holiday) => holiday.id !== holidayId));
  };

  const handleImportFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        if (jsonData.length === 0) {
          toast.error("Empty File", {
            description: "The Excel file is empty or has no data.",
          });
          return;
        }

        // Validate and parse the data
        const importedHolidays = [];
        const errors = [];

        jsonData.forEach((row, index) => {
          const dateStr = row.date || row.Date || row.DATE;
          const holidayName =
            row.holiday_name ||
            row["Holiday Name"] ||
            row["Holiday_Name"] ||
            row.holidayName;

          if (!dateStr || !holidayName) {
            errors.push(`Row ${index + 2}: Missing date or holiday name`);
            return;
          }

          // Parse date in d/m format (e.g., "15/1", "25/12")
          const dateParts = dateStr.toString().split("/");
          if (dateParts.length !== 2) {
            errors.push(
              `Row ${index + 2}: Invalid date format. Expected d/m format (e.g., 15/1, 25/12)`,
            );
            return;
          }

          const day = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]);

          if (
            isNaN(day) ||
            isNaN(month) ||
            day < 1 ||
            day > 31 ||
            month < 1 ||
            month > 12
          ) {
            errors.push(`Row ${index + 2}: Invalid date values`);
            return;
          }

          // Create date string in YYYY-MM-DD format using the calendar year
          const formattedDate = `${calendarData.year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

          importedHolidays.push({
            id: Date.now() + index,
            name: holidayName.toString().trim(),
            date: formattedDate,
          });
        });

        if (errors.length > 0) {
          toast.warning("Import Warnings", {
            description: `Some rows had errors: ${errors.slice(0, 3).join(", ")}${errors.length > 3 ? ` ...and ${errors.length - 3} more` : ""}`,
            duration: 5000,
          });
        }

        if (importedHolidays.length > 0) {
          // Merge with existing holidays (avoid duplicates)
          const existingDates = new Set(holidays.map((h) => h.date));
          const newHolidays = importedHolidays.filter(
            (h) => !existingDates.has(h.date),
          );
          setHolidays([...holidays, ...newHolidays]);

          toast.success("Imported!", {
            description: `${newHolidays.length} holiday(s) imported successfully.${importedHolidays.length - newHolidays.length > 0 ? ` ${importedHolidays.length - newHolidays.length} duplicate(s) skipped.` : ""}`,
          });
        }

        setShowImportModal(false);
        setImportType(null);
        // Reset file input
        event.target.value = "";
      } catch (error) {
        console.error("Error importing Excel:", error);
        toast.error("Import Failed", {
          description:
            "Failed to import holidays from Excel. Please check the file format.",
        });
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImportFromCalendar = () => {
    if (!selectedCalendarId) {
      toast.warning("No Calendar Selected", {
        description: "Please select a calendar to import from.",
      });
      return;
    }

    if (!selectedCalendarData) {
      toast.error("Error", {
        description: "Failed to load calendar data. Please try again.",
      });
      return;
    }

    const sourceHolidays =
      selectedCalendarData.holidays ||
      selectedCalendarData.calendar?.holidays ||
      [];

    if (sourceHolidays.length === 0) {
      toast.warning("No Holidays", {
        description: "The selected calendar has no holidays to import.",
      });
      return;
    }

    // Convert holidays to new year format
    const importedHolidays = sourceHolidays.map((holiday, index) => {
      const sourceDate = new Date(holiday.date);
      // Keep the same month and day, but change to the new calendar year
      const formattedDate = `${calendarData.year}-${String(sourceDate.getMonth() + 1).padStart(2, "0")}-${String(sourceDate.getDate()).padStart(2, "0")}`;

      return {
        id: Date.now() + index,
        name: holiday.title || holiday.name,
        date: formattedDate,
      };
    });

    // Merge with existing holidays (avoid duplicates)
    const existingDates = new Set(holidays.map((h) => h.date));
    const newHolidays = importedHolidays.filter(
      (h) => !existingDates.has(h.date),
    );
    setHolidays([...holidays, ...newHolidays]);

    toast.success("Imported!", {
      description: `${newHolidays.length} holiday(s) imported successfully.${importedHolidays.length - newHolidays.length > 0 ? ` ${importedHolidays.length - newHolidays.length} duplicate(s) skipped.` : ""}`,
    });

    setShowImportModal(false);
    setImportType(null);
    setSelectedCalendarId("");
  };

  const handleEditUser = (userId) => {
    const user = users.find((u) => u.id === userId);

    if (user) {
      setSelectedUser(user);
      setUserEditForm({
        casualLeave: user.casualLeave,
        earnedLeave: user.earnedLeave,
      });
      setShowUserEditModal(true);
    }
  };

  const handleSaveUserEdit = () => {
    if (!selectedUser) return;

    // Update the user in the users array
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === selectedUser.id
          ? {
            ...user,
            casualLeave: userEditForm.casualLeave,
            earnedLeave: userEditForm.earnedLeave,
          }
          : user,
      ),
    );

    setShowUserEditModal(false);
    setSelectedUser(null);
    setUserEditForm({ casualLeave: 0, earnedLeave: 0 });
  };

  const handleDeleteUser = (userId) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    setUserToDelete(user);
    setDeleteUserAlertOpen(true);
  };

  const confirmDeleteUser = () => {
    if (userToDelete) {
      setUsers((prevUsers) =>
        prevUsers.filter((u) => u.id !== userToDelete.id),
      );

      toast.success("Removed!", {
        description: `${userToDelete.name} has been removed from the calendar.`,
      });

      setUserToDelete(null);
      setDeleteUserAlertOpen(false);
    }
  };

  const handleCancelUserEdit = () => {
    setShowUserEditModal(false);
    setSelectedUser(null);
    setUserEditForm({ casualLeave: 0, earnedLeave: 0 });
  };

  const handleCreateCalendar = async () => {
    try {
      // Validate year before creating calendar
      const isYearValid = await validateYear(calendarData.year);
      if (!isYearValid) {
        toast.error("Invalid Year", {
          description: yearError || "Please enter a valid year",
        });
        return;
      }

      const createPromise = async () => {
        // Prepare calendar data for API
        const calendarPayload = {
          name: calendarData.name,
          year: calendarData.year,
          academy_id: actualAcademyId,
          is_saturday_weekend: calendarData.weekendSelection.saturday,
          is_sunday_weekend: calendarData.weekendSelection.sunday,
          earned_leave_incremental_hour: calendarData.earnedLeaveIncrement * 8, // Convert days to hours
          max_overtime_hour: calendarData.maxOvertimeHours,
          holidays: holidays.map((holiday) => ({
            name: holiday.name,
            date: holiday.date,
            is_recurring: holiday.isRecurring,
            description: holiday.description,
          })),
        };

        const result = await createCalendar(calendarPayload).unwrap();

        // Navigate back to calendar page based on user role
        const pathSegments = window.location.pathname
          .split("/")
          .filter(Boolean);
        const roleBasePath = pathSegments[0] || "admin";
        navigate(`/${roleBasePath}/calendar`);

        return result;
      };

      toast.promise(createPromise(), {
        loading: "Creating Calendar...",
        success: "Calendar created successfully!",
        error: (err) => {
          console.error("Create calendar error:", err);
          return (
            err?.data?.message || "Failed to create calendar. Please try again."
          );
        },
      });
    } catch (error) {
      console.error("Error creating calendar:", error);
      // Error is handled by toast.promise
    }
  };

  // Show loading toast for calendar data
  // useEffect(() => {
  //   let toastId;
  //   if (isLoadingCalendars || isLoadingAcademies) {
  //     toastId = toast.loading("Loading calendar data...");
  //   }
  //   return () => {
  //     if (toastId) {
  //       toast.dismiss(toastId);
  //     }
  //   };
  // }, [isLoadingCalendars, isLoadingAcademies]);

  const renderStep1 = () => (
    <div className="p-8">
      <h2 className="text-2xl font-semibold text-gray-900 mb-8">
        Basic Information
      </h2>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Calendar Name *
          </label>
          <input
            type="text"
            value={calendarData.name}
            onChange={(e) =>
              setCalendarData({ ...calendarData, name: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
            placeholder="Enter calendar name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Year *
          </label>
          <input
            type="number"
            value={calendarData.year}
            onChange={(e) => {
              const year = parseInt(e.target.value);
              const newYear = year || currentYear;

              setCalendarData({
                ...calendarData,
                year: newYear,
              });

              // Immediately validate past year (no API call needed)
              if (newYear < currentYear) {
                setYearError(
                  `Year must be ${currentYear} or later. Past years are not allowed.`,
                );
              } else {
                // For valid years, optimistically clear error immediately
                // The useEffect will check for duplicates when the query completes
                setYearError("");
              }
            }}
            onBlur={() => {
              // Final validation when user leaves the field
              if (calendarData.year) {
                validateYear(calendarData.year);
              }
            }}
            className={`w-full px-4 py-3 border rounded focus:outline-none focus:ring-2 focus:border-black text-sm ${yearError
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-black"
              }`}
            min={currentYear}
            max={currentYear + 10}
            placeholder={`Enter year (${currentYear} or later)`}
          />
          {yearError && (
            <p className="mt-1 text-sm text-red-600">{yearError}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">
            Weekend Selection
          </label>
          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={calendarData.weekendSelection.saturday}
                onChange={(e) => {
                  setCalendarData({
                    ...calendarData,
                    weekendSelection: {
                      ...calendarData.weekendSelection,
                      saturday: e.target.checked,
                    },
                  });
                }}
                className="w-4 h-4 text-black border-gray-300 focus:ring-black rounded"
              />
              <span className="text-sm font-medium text-gray-700">
                Saturday
              </span>
            </label>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={calendarData.weekendSelection.sunday}
                onChange={(e) => {
                  setCalendarData({
                    ...calendarData,
                    weekendSelection: {
                      ...calendarData.weekendSelection,
                      sunday: e.target.checked,
                    },
                  });
                }}
                className="w-4 h-4 text-black border-gray-300 focus:ring-black rounded"
              />
              <span className="text-sm font-medium text-gray-700">Sunday</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8 p-8">
      {/* Holidays Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">Holidays</h2>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200"
              >
                <Upload className="w-4 h-4" />
                <span>Import</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => setShowHolidayModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded hover:bg-zinc-700 transition-colors duration-200"
            >
              <Plus className="w-4 h-4" />
              <span>Add Holiday</span>
            </button>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {holidays.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Holidays
              </h3>
              <p className="text-gray-500">
                No holidays have been added for {calendarData.year} yet.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {holidays.map((holiday) => (
                <div
                  key={holiday.id}
                  className="p-4 hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full bg-red-100" />
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {holiday.name}
                        </h4>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-gray-900">
                        {new Date(holiday.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDeleteHoliday(holiday.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Leave and Overtime Configuration */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          Leave and Overtime Configuration
        </h3>
        <div className="space-y-6">
          {/* Leave Configuration - Two columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Casual Leave per Year (days)
              </label>
              <input
                type="number"
                step="0.5"
                value={calendarData.casualLeavePerYear}
                onChange={(e) =>
                  setCalendarData({
                    ...calendarData,
                    casualLeavePerYear: parseFloat(e.target.value),
                  })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Earned Leave Increment (days/month)
              </label>
              <input
                type="number"
                step="0.5"
                value={calendarData.earnedLeaveIncrement}
                onChange={(e) =>
                  setCalendarData({
                    ...calendarData,
                    earnedLeaveIncrement: parseFloat(e.target.value),
                  })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
                min="0"
              />
            </div>
          </div>

          {/* Overtime Configuration - Single column */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Overtime Hours per Month
            </label>
            <input
              type="number"
              value={calendarData.maxOvertimeHours}
              onChange={(e) =>
                setCalendarData({
                  ...calendarData,
                  maxOvertimeHours: parseInt(e.target.value),
                })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
              min="0"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const filteredUsers = users.filter(
      (user) =>
        user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.employeeId.toLowerCase().includes(userSearchTerm.toLowerCase()),
    );

    return (
      <div className="p-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-8">
          User Leave Balances
        </h2>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search users by name, email, or employee ID..."
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
          />
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {users.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No employees to add from this academy
              </h3>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No users match your search
              </h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profile
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Casual Leave
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Earned Leave
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 relative">
                            {user.profile ? (
                              <img
                                className="h-10 w-10 rounded-full"
                                src={user.profile}
                                alt={user.name}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-zinc-200 flex items-center justify-center">
                                <span className="text-sm font-medium text-zinc-600">
                                  {user.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .toUpperCase()}
                                </span>
                              </div>
                            )}
                            {/* "Me" tag for current user */}
                            {user.id === (userInfo?.userId || userInfo?.id) && (
                              <span className="absolute -top-1 -right-3 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full font-medium">
                                Me
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.employeeId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.casualLeave} days
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.earnedLeave} days
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditUser(user.id)}
                            className="px-3 py-1 bg-blue-100 text-blue-800 font-semibold rounded hover:bg-blue-200 transition-colors duration-200"
                          >
                            Edit
                          </button>
                          {/* Only show remove button if not current user */}
                          {user.id !== (userInfo?.userId || userInfo?.id) && (
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="px-3 py-1 bg-red-100 text-red-800 font-semibold rounded hover:bg-red-200 transition-colors duration-200"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Form validation
  const isStep1Valid = () => {
    return (
      calendarData.name.trim().length >= 2 &&
      calendarData.year >= currentYear &&
      calendarData.year <= currentYear + 10 &&
      !yearError
    );
  };

  const isStep2Valid = () => {
    return true; // Step 2 doesn't require validation for now
  };

  const isStep3Valid = () => {
    return true; // Step 3 doesn't require validation for now
  };

  const canProceedToNext = () => {
    if (currentStep === 1) return isStep1Valid();
    if (currentStep === 2) return isStep2Valid();
    if (currentStep === 3) return isStep3Valid();
    return false;
  };

  return (
    <div className="h-full bg-white flex flex-col">
      {/* Header - Sticky */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center justify-between py-4 px-6">
          <button
            onClick={handleBack}
            className="flex items-center p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="Back to Calendar"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          {/* Progress Steps */}
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${currentStep >= step
                      ? "bg-black text-white"
                      : "bg-gray-200 text-gray-600"
                    }`}
                >
                  {step}
                </div>
                {step < 3 && (
                  <div
                    className={`w-12 h-1 mx-2 ${currentStep > step ? "bg-black" : "bg-gray-200"
                      }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>
        <div className="px-6 pb-4">
          <div className="text-sm text-gray-600 text-center">
            Step {currentStep} of 3: {currentStep === 1 && "Basic Information"}
            {currentStep === 2 && "Holidays & Configuration"}
            {currentStep === 3 && "User Management"}
          </div>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>
      </div>

      {/* Navigation - Sticky */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={currentStep === 1 ? handleBack : handlePrevious}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors duration-200"
            >
              {currentStep === 1 ? "Cancel" : "Previous"}
            </button>

            <div className="flex items-center space-x-3">
              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  disabled={!canProceedToNext()}
                  className={`px-6 py-2 rounded transition-colors duration-200 ${canProceedToNext()
                      ? "bg-black text-white hover:bg-gray-800"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleCreateCalendar}
                  disabled={isCreating}
                  className={`px-6 py-2 rounded transition-colors duration-200 flex items-center space-x-2 ${isCreating
                      ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                      : "bg-black text-white hover:bg-gray-800"
                    }`}
                >
                  {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{isCreating ? "Creating..." : "Create Calendar"}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Alert Dialog for User Deletion */}
      <AlertDialog
        open={deleteUserAlertOpen}
        onOpenChange={setDeleteUserAlertOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {userToDelete?.name} from the
              calendar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, remove!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Holiday Modal */}
      {showHolidayModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowHolidayModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Add Holiday
              </h3>
              <button
                onClick={() => setShowHolidayModal(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Holiday Name *
                </label>
                <input
                  type="text"
                  value={holidayForm.name}
                  onChange={(e) =>
                    setHolidayForm({ ...holidayForm, name: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black ${formErrors.name ? "border-red-500" : "border-gray-300"
                    }`}
                  placeholder="Enter holiday name"
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={holidayForm.date}
                    onChange={(e) =>
                      setHolidayForm({ ...holidayForm, date: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black text-sm cursor-pointer ${formErrors.date
                        ? "border-red-500"
                        : holidayForm.date
                          ? "border-gray-300 text-gray-900"
                          : "border-gray-300 text-zinc-500"
                      }`}
                    style={{
                      colorScheme: "light",
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                    }}
                    onClick={(e) => {
                      e.target.showPicker && e.target.showPicker();
                    }}
                  />
                </div>
                {formErrors.date && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.date}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowHolidayModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveHoliday}
                className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200"
              >
                Add Holiday
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Edit Modal */}
      {showUserEditModal && selectedUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCancelUserEdit();
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit Leave Balance
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedUser.name} - {selectedUser.employeeId}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Casual Leave (days)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={userEditForm.casualLeave}
                    onChange={(e) =>
                      setUserEditForm({
                        ...userEditForm,
                        casualLeave: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Earned Leave (days)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={userEditForm.earnedLeave}
                    onChange={(e) =>
                      setUserEditForm({
                        ...userEditForm,
                        earnedLeave: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
                    min="0"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={handleCancelUserEdit}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUserEdit}
                  className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowImportModal(false);
              setImportType(null);
              setSelectedCalendarId("");
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Import Holidays
              </h3>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportType(null);
                  setSelectedCalendarId("");
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {!importType ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Choose how you want to import holidays:
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => setImportType("excel")}
                      className="w-full flex items-center justify-between p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Upload className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <div className="font-medium text-gray-900">
                            Import from Excel
                          </div>
                          <div className="text-sm text-gray-500">
                            Upload an Excel file with holidays
                          </div>
                        </div>
                      </div>
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    </button>

                    <button
                      onClick={() => setImportType("calendar")}
                      className="w-full flex items-center justify-between p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <CalendarIcon className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="text-left">
                          <div className="font-medium text-gray-900">
                            Import from Existing Calendar
                          </div>
                          <div className="text-sm text-gray-500">
                            Copy holidays from another calendar
                          </div>
                        </div>
                      </div>
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                </>
              ) : importType === "excel" ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Excel File
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Expected format: Columns should be "date" (d/m format) and
                      "holiday_name"
                    </p>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleImportFromExcel}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black text-sm"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setImportType(null);
                    }}
                    className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors duration-200"
                  >
                    Back
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Calendar
                    </label>
                    {isLoadingCalendars ? (
                      <div className="text-center py-4">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
                      </div>
                    ) : existingCalendars.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No existing calendars found.
                      </p>
                    ) : (
                      <select
                        value={selectedCalendarId}
                        onChange={(e) => setSelectedCalendarId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black text-sm"
                      >
                        <option value="">Select a calendar...</option>
                        {existingCalendars
                          .filter((cal) => cal.year !== calendarData.year) // Exclude current year
                          .map((calendar) => (
                            <option
                              key={calendar._id || calendar.id}
                              value={calendar._id || calendar.id}
                            >
                              {calendar.name} ({calendar.year})
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setImportType(null);
                        setSelectedCalendarId("");
                      }}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors duration-200"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleImportFromCalendar}
                      disabled={!selectedCalendarId || isLoadingCalendars}
                      className={`flex-1 px-4 py-2 rounded transition-colors duration-200 ${!selectedCalendarId || isLoadingCalendars
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-black text-white hover:bg-gray-800"
                        }`}
                    >
                      Import
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Alert Dialog */}
      <AlertDialog
        open={deleteUserAlertOpen}
        onOpenChange={setDeleteUserAlertOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              <span className="font-semibold">{userToDelete?.name}</span> from
              the calendar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeleteUserAlertOpen(false);
                setUserToDelete(null);
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors duration-200"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteUser}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors duration-200"
            >
              Yes, remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CreateCalendar;
